import { cached } from '@glimmer/tracking';

import type { DSData, DSSetup } from 'webapp-types';

import DsDataOutputBase from 'webapp/models-custom/ds-data/output/base';
import type DsDataOutputCompare from 'webapp/models-custom/ds-data/output/compare';
import DsDataPointTopLevel from 'webapp/models-custom/ds-data/point/top';
import type DsDataRootTopLevel from 'webapp/models-custom/ds-data/root/top';

export interface MetricSelectionData {
	dsId: number;
	primaryDsId: number | null;
	metricName: string;
	dimensionValues: DSData.DimensionsAndValuesMap;
	unit: string;
	metricSetupData: DSSetup.MetricSetupData | null;
}

export default class DsDataOutputTopLevel extends DsDataOutputBase {
	dataRoot: DsDataRootTopLevel;
	points: DsDataPointTopLevel[];

	constructor(dataRoot: DsDataRootTopLevel, rawData: DSData.PointGroupTopLevel) {
		super(rawData);
		this.dataRoot = dataRoot;
		this.points = (rawData.items || []).map(
			(pointData) => new DsDataPointTopLevel(this, pointData),
		);
	}

	// used when we know this is a number
	get point(): DsDataPointTopLevel | null {
		return this.points[0] || null;
	}

	@cached
	get compareOutputs() {
		const outputs: DsDataOutputCompare[] = [];
		this.dataRoot.compareRoots.forEach((compareRoot) => {
			const compareOutput = compareRoot.getOutputByDimensionValuesAndUnit(
				this.dimensionValues,
				this.unit,
			)!;
			if (compareOutput) {
				outputs.push(compareOutput);
			}
		});
		return outputs;
	}

	get metricSelectionData(): MetricSelectionData {
		return {
			dsId: this.dataRoot.dataSelect.numericId!,
			primaryDsId: this.dataRoot.dataSelect.updateFrom,
			metricName: this.dataRoot.metricName,
			dimensionValues: this.dimensionValues,
			unit: this.unit,
			metricSetupData: this.dataRoot.dataSelect.metricSetupData,
		};
	}

	override getPointForIndex(index: number) {
		return super.getPointForIndex(index) as DsDataPointTopLevel | null;
	}

	getDataRootMediaMapUrl(): string | undefined {
		// search through all dimension values
		let mediaUrl = '';
		const mediaMap = this.dataRoot.mediaMap;
		Object.values(this.dimensionValues).some((attribute) => {
			mediaUrl = mediaMap[attribute] || '';
			if (mediaUrl) {
				return true;
			}
		});
		return mediaUrl;
	}
}
